import React, {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import api from "@/api";
import ReportGenerator from "./ReportGenerator";
import { formatReportValue } from "./reportFields";

const REPORT_FETCH_BATCH = 200;

// The report on screen fetches every trial with all its data: it shows at most this many of the selection (the
// Excel export takes the whole selection)
export const MAX_SHOWN_TRIALS = 1000;

const ShowReport = forwardRef(({ trialIDs: selectedIDs = [], fields = [], onReport, hideEmptyTaskRows = true, onHideEmptyTaskRowsChange }, ref) => {
  const trialIDs = selectedIDs.length > MAX_SHOWN_TRIALS ? selectedIDs.slice(0, MAX_SHOWN_TRIALS) : selectedIDs;
  const limitNotice = selectedIDs.length > MAX_SHOWN_TRIALS && (
    <p role="note" data-testid="report-limit" className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
      {`${selectedIDs.length.toLocaleString("en-US")} trials are selected; the report shows the first ${MAX_SHOWN_TRIALS.toLocaleString("en-US")}. Export to Excel takes all of them.`}
    </p>
  );
  const [trials, setTrials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Built from the fetched trials, so "Hide empty task rows" applies without fetching again
  const fieldsKey = JSON.stringify(fields);
  const report = useMemo(
    () => (trials?.length && fields.length ? ReportGenerator({ trials, fields, hideEmptyTaskRows }) : null),
    // fields is read via its content key (the parent rebuilds the array on every render)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trials, fieldsKey, hideEmptyTaskRows]
  );

  // Lets the parent enable its export once there is something to export
  useEffect(() => {
    onReport?.(loading ? null : report);
  }, [report, loading, onReport]);

  useImperativeHandle(
    ref,
    () => ({
      report,
    }),
    [report]
  );

  // The parent may rebuild trialIDs on every render; key the fetch on its contents so a re-render
  // doesn't refetch the whole selection
  const trialIDsKey = trialIDs.join(",");

  useEffect(() => {
    let cancelled = false;
    if (!fields.length || !trialIDs.length) {
      setTrials(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    // Fetch the trials in batches, one at a time: each one comes with all its data, so a single
    // request for hundreds of trials would exceed the API's memory limit (and URL length).
    const fetchAll = async () => {
      const fetched = [];
      for (let i = 0; i < trialIDs.length && !cancelled; i += REPORT_FETCH_BATCH) {
        const ids = trialIDs.slice(i, i + REPORT_FETCH_BATCH);
        // A path relative to the api instance's baseURL (which also adds the token): prefixing
        // config.apiBaseUrl as well gave /v1/v1/trials/... where that base URL is relative (production)
        const res = await api.post(`/trials/${ids.join(",")}`, {});
        // One id hits POST /trials/{id}, which returns a single object
        const data = res?.data;
        fetched.push(...(Array.isArray(data) ? data : data ? [data] : []));
      }
      return fetched;
    };

    fetchAll()
      .then((fetched) => {
        if (cancelled) return;
        setTrials(fetched);
        if (!fetched.length) setError("Trial not found");
      })
      .catch((err) => {
        if (cancelled) return;
        console.log("ShowReport response error:", err);
        setTrials(null);
        setError("Failed to fetch trial");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
    // fields/trialIDs are read via their content keys (see above)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsKey, trialIDsKey]);

  if (loading)
    return (
      <>
        {limitNotice}
        <div className="py-8 text-center text-gray-500 dark:text-gray-400">
          Loading report...
        </div>
      </>
    );
  if (error)
    return (
      <div className="py-8 text-center text-red-600 dark:text-red-400">
        {error}
      </div>
    );
  if (!report || !report.rows.length)
    return (
      <div className="py-8 text-center text-gray-500 dark:text-gray-400">
        No data found.
      </div>
    );

  const count = (n, word) => `${n.toLocaleString("en-US")} ${word}${n === 1 ? "" : "s"}`;
  const hidden = report.hiddenRows ? ` ${count(report.hiddenRows, "empty task row")} hidden.` : "";

  return (
    <>
    {limitNotice}
    <div className="mb-2 flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
      <p data-testid="report-summary" className="text-sm text-gray-600 dark:text-gray-300">
        {report.perDiaryEntry
          ? `${count(report.rows.length, "row")} for ${count(report.trialCount, "trial")}: one row per diary entry, because the layout has task fields. VA No and Task are shown after Trial No to tell the rows apart.${hidden}`
          : `${count(report.rows.length, "row")}, one per trial.`}
      </p>
      {report.perDiaryEntry && (
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={hideEmptyTaskRows}
            onChange={(e) => onHideEmptyTaskRowsChange?.(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          Hide empty task rows
        </label>
      )}
    </div>
    {/* As high as the screen allows, so the horizontal scrollbar is always at the bottom of the window, not below
        the last row (customer point 17); the header row stays at the top while the rows scroll */}
    <div data-testid="report-table-scroll" className="max-h-[calc(100dvh-12rem)] min-h-64 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow">
      <table className="min-w-full align-middle text-sm whitespace-nowrap border-separate border-spacing-0">
        <thead>
          <tr>
            {report.columns.map((column) => (
              <th
                key={column.key}
                title={column.key}
                className={`sticky top-0 z-10 bg-gray-100 px-3 py-4 font-semibold text-gray-900 dark:bg-gray-700 dark:text-gray-50 ${column.type === "number" ? "text-right" : "text-left"}`}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.rows.map((row, idx) => (
            <tr
              key={idx}
              className="border-b border-gray-100 dark:border-gray-700/50"
            >
              {row.map((val, i) => {
                const column = report.columns[i];
                const text = formatReportValue(val, column);
                return (
                  <td key={i} className={`px-3 py-2 ${column.type === "number" ? "text-right tabular-nums" : ""}`}>
                    {text === "" ? <span className="text-gray-400 dark:text-gray-500" aria-label="missing">–</span> : text}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
});

export default ShowReport;
